/**
 * MasterDemoAccounts — surface the 5 named demo profiles so the operator can:
 *   • see Trial Demo (locked UI for prospect testing)
 *   • see 4 Growth-plan default users (typical paying customer)
 *   • copy login credentials with a click
 *   • impersonate any of them in a new tab to preview the experience
 *   • re-seed (refresh grants) or rotate passwords on demand
 *
 * Backed by:
 *   POST /api/master/demo-accounts/seed?reset_passwords=
 *   GET  /api/master/demo-accounts
 *   POST /api/master/tenants/{id}/impersonate
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  UserCog, Copy, RefreshCw, KeyRound, ExternalLink,
  Loader2, ShieldCheck, Lock, Send, Wand2, Mail, Activity, ChevronRight,
  MessageCircle,
} from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_PW = "AgenteLeadDemo2026";

export default function MasterDemoAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [recentPasswords, setRecentPasswords] = useState({}); // email -> plaintext (after reset)
  const [sendingFor, setSendingFor] = useState(null);

  const [shareModal, setShareModal] = useState(null); // {slug, label}
  const [shareForm, setShareForm] = useState({ to: "", recipient_name: "", custom_message: "", lang: "pt", ttl_days: 14 });
  const [shareBusy, setShareBusy] = useState(false);

  const openShare = (slug, label) => {
    setShareModal({ slug, label });
    setShareForm({ to: "", recipient_name: "", custom_message: "", lang: "pt", ttl_days: 14 });
  };

  const sendShareTrial = async () => {
    if (!/^\S+@\S+\.\S+$/.test(shareForm.to)) {
      toast.error("Insira um e-mail válido para o destinatário.");
      return;
    }
    setShareBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/master/demo-accounts/${shareModal.slug}/share-trial`,
        {
          to: shareForm.to.trim(),
          recipient_name: shareForm.recipient_name.trim() || null,
          custom_message: shareForm.custom_message.trim() || null,
          lang: shareForm.lang,
          ttl_days: shareForm.ttl_days,
        },
      );
      toast.success(`Trial enviado para ${data.to} (${data.ttl_days}d magic link)`);
      setShareModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setShareBusy(false); }
  };

  const [timelineFor, setTimelineFor] = useState(null); // {slug, label}
  const [timelineItems, setTimelineItems] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const openTimeline = async (slug, label) => {
    setTimelineFor({ slug, label });
    setTimelineLoading(true); setTimelineItems([]);
    try {
      const { data } = await axios.get(`${API}/master/demo-accounts/${slug}/activity?limit=80`);
      setTimelineItems(data.timeline || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setTimelineLoading(false); }
  };

  const generateMagicLink = async (slug, label) => {
    try {
      const { data } = await axios.post(`${API}/auth/magic-link?slug=${slug}&ttl_days=14`);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success(`Magic link for ${label} copied · 14d valid`);
      } catch {
        // Clipboard blocked — show modal-style alert
        window.prompt("Magic link (copy manually):", data.url);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const shareViaWhatsApp = async (slug, label) => {
    const phone = window.prompt(
      `Compartilhar via WhatsApp para qual número?\n\n• Inclua o DDI (ex: 5511999998888)\n• Apenas dígitos — sem +, espaços ou parênteses\n• Deixe em branco para escolher o contato dentro do WhatsApp`,
      "",
    );
    if (phone === null) return;
    const cleanPhone = (phone || "").replace(/\D/g, "");
    try {
      const { data } = await axios.post(`${API}/auth/magic-link?slug=${slug}&ttl_days=14`);
      const msg = `Olá! 👋\n\nEsse é o seu acesso direto ao *AgenteLead Pro* (${label}). Clique no link abaixo — sem senha, sem cadastro, sua sessão fica ativa por 14 dias:\n\n${data.url}\n\nQualquer dúvida me chama por aqui.`;
      const wa = cleanPhone
        ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`
        : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(wa, "_blank", "noopener,noreferrer");
      toast.success(cleanPhone
        ? `WhatsApp aberto para ${cleanPhone}`
        : `WhatsApp aberto — escolha o contato`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const sendWelcome = async (slug, label) => {
    const recipient = window.prompt(
      `Send the welcome email for "${label}" to which address?\n\n(Leave blank to send to the demo's own inbox.)`,
      "",
    );
    if (recipient === null) return;
    const rotate = window.confirm(
      "Rotate to a fresh random password before sending? Click OK for a new one (the email will include it), or Cancel to send the shared default.",
    );
    setSendingFor(slug);
    try {
      const { data } = await axios.post(
        `${API}/master/demo-accounts/${slug}/send-welcome`,
        { to: recipient || null, lang: "pt", rotate_password: rotate },
      );
      if (rotate && data.password) {
        const acct = accounts.find((a) => a.slug === slug);
        if (acct) {
          setRecentPasswords((p) => ({ ...p, [acct.email]: data.password }));
        }
      }
      toast.success(
        rotate
          ? `Welcome email sent to ${data.to} · password rotated`
          : `Welcome email sent to ${data.to}`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setSendingFor(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/master/demo-accounts`);
      setAccounts(data.accounts || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const seed = async (resetPasswords = false) => {
    if (resetPasswords && !window.confirm("Rotate every demo password? You'll see the new ones once.")) return;
    if (resetPasswords) setResetting(true); else setSeeding(true);
    try {
      const { data } = await axios.post(
        `${API}/master/demo-accounts/seed?reset_passwords=${resetPasswords}`,
      );
      const pwMap = {};
      for (const a of data.accounts || []) {
        if (a.password) pwMap[a.email] = a.password;
      }
      setRecentPasswords(pwMap);
      toast.success(resetPasswords
        ? `Reset ${Object.keys(pwMap).length} demo password(s)`
        : `Seeded/refreshed ${data.accounts?.length || 0} demo account(s)`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setSeeding(false); setResetting(false); }
  };

  const impersonate = async (tenantId, label, sameTab = false) => {
    try {
      const { data } = await axios.post(`${API}/master/tenants/${tenantId}/impersonate`);
      if (!data?.access_token) { toast.error("No token returned"); return; }
      const url = `/impersonate?token=${encodeURIComponent(data.access_token)}&dest=/dashboard${sameTab ? "&backup=1" : ""}`;
      if (sameTab) { window.location.href = url; }
      else { window.open(url, "_blank", "noopener"); toast.success(`Impersonating ${label}`); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error("Clipboard blocked"); }
  };

  return (
    <MasterLayout title="Demo Profiles">
      <div className="px-6 py-6 space-y-5" data-testid="master-demo-accounts">
        {/* Header card */}
        <div className="rounded-2xl border p-5"
          style={{ borderColor: "rgba(201,162,78,0.25)", background: "linear-gradient(135deg, rgba(201,162,78,0.08), rgba(124,58,237,0.05))" }}>
          <div className="flex items-start gap-3">
            <UserCog className="w-6 h-6 text-[#C9A24E] mt-0.5 shrink-0" />
            <div className="flex-1">
              <h1 className="text-[20px] font-bold text-white">Demo Profiles</h1>
              <p className="text-[13px] text-white/70 mt-1 leading-relaxed">
                5 contas prontas para você compartilhar com prospects. <b>User 1</b> está
                em modo Trial (bloqueios + blur ativos) — ideal pra mandar o link e o lead
                ver a experiência travada. Os demais (<b>2-5</b>) usam o plano <b>Growth</b>
                por padrão; ajuste agentes/módulos individualmente em <b>Clients</b>.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button onClick={() => seed(false)} disabled={seeding || resetting}
                  data-testid="seed-demo-btn"
                  className="h-9 px-4 rounded-lg bg-[#C9A24E] hover:bg-[#b48d3e] text-[#1A0E36] font-bold text-[12.5px] inline-flex items-center gap-2 disabled:opacity-60">
                  {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Seed / refresh accounts
                </button>
                <button onClick={() => seed(true)} disabled={seeding || resetting}
                  data-testid="reset-demo-passwords-btn"
                  className="h-9 px-4 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 border border-rose-400/40 text-rose-200 font-bold text-[12.5px] inline-flex items-center gap-2 disabled:opacity-60">
                  {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  Rotate passwords
                </button>
                <span className="text-[11.5px] text-white/55 ml-2">
                  Default password: <code className="px-2 py-0.5 rounded bg-white/10">{DEFAULT_PW}</code>
                </span>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/50">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading demo accounts…
          </div>
        ) : accounts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.03] p-10 text-center text-white/60 text-[13px]">
            No demo accounts yet. Click <b className="text-[#C9A24E]">Seed / refresh accounts</b> to create them.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {accounts.map((a) => {
              const pw = recentPasswords[a.email] || DEFAULT_PW;
              const isTrial = a.package === "personalizado";
              return (
                <div key={a.tenant_id}
                  data-testid={`demo-card-${a.slug}`}
                  className="rounded-xl border bg-white/[0.04] p-4 hover:bg-white/[0.06] transition-colors"
                  style={{ borderColor: isTrial ? "rgba(244,63,94,0.45)" : "rgba(16,185,129,0.45)" }}>
                  <div className="flex items-start gap-2 mb-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isTrial ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                      {isTrial ? <Lock className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-bold text-white truncate">{a.label}</div>
                      <div className="text-[11.5px] text-white/55 mt-0.5">
                        {a.modules?.length || 0} modules · {a.agents?.length || 0} agents · {a.leads_count} leads
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${isTrial ? "bg-rose-500/20 text-rose-200" : "bg-emerald-500/20 text-emerald-200"}`}>
                      {a.package}
                    </span>
                  </div>

                  <div className="space-y-2 text-[12px]">
                    <CredRow label="email" value={a.email} onCopy={() => copy(a.email, "Email")} />
                    <CredRow label="password" value={pw} onCopy={() => copy(pw, "Password")} mono />
                  </div>

                  {/* Engagement strip — Hotjar light */}
                  <button onClick={() => openTimeline(a.slug, a.label)}
                    data-testid={`timeline-${a.slug}`}
                    className="w-full mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 transition-colors text-left">
                    <Activity className={`w-3.5 h-3.5 ${a.actions_24h_total > 0 ? "text-emerald-400" : "text-white/35"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Engagement (24h)</div>
                      <div className="text-[11.5px] text-white/85 truncate">
                        {a.actions_24h_total > 0
                          ? `${a.actions_24h_total} ações · last: ${a.last_action || "—"}`
                          : "Sem atividade ainda"}
                      </div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-white/45" />
                  </button>

                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
                    <button onClick={() => impersonate(a.tenant_id, a.label, true)}
                      data-testid={`impersonate-here-${a.slug}`}
                      title="Abrir nesta aba (banner amarelo de retorno)"
                      className="h-9 px-3 rounded-lg bg-amber-400 hover:bg-amber-500 text-[#1A0E36] font-bold text-[12px] inline-flex items-center justify-center gap-1.5">
                      Here
                    </button>
                    <button onClick={() => impersonate(a.tenant_id, a.label, false)}
                      data-testid={`impersonate-${a.slug}`}
                      className="flex-1 h-9 px-3 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-[12px] inline-flex items-center justify-center gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5" /> New tab
                    </button>
                    <button onClick={() => openShare(a.slug, a.label)}
                      data-testid={`share-trial-${a.slug}`}
                      title="Send a 14-day magic link via Resend"
                      className="flex-1 h-9 px-3 rounded-lg bg-[#C9A24E] hover:bg-[#b48d3e] text-[#1A0E36] font-bold text-[12px] inline-flex items-center justify-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" /> Share Trial
                    </button>
                    <button onClick={() => shareViaWhatsApp(a.slug, a.label)}
                      data-testid={`share-whatsapp-${a.slug}`}
                      title="Compartilhar magic link via WhatsApp (wa.me)"
                      className="h-9 px-3 rounded-lg bg-[#25D366] hover:bg-[#1FB857] text-white font-bold text-[12px] inline-flex items-center justify-center gap-1.5">
                      <MessageCircle className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">WhatsApp</span>
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => generateMagicLink(a.slug, a.label)}
                      data-testid={`magic-link-${a.slug}`}
                      title="Just copy the magic link"
                      className="flex-1 h-8 px-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/75 text-[11px] font-bold inline-flex items-center justify-center gap-1.5">
                      <Wand2 className="w-3 h-3" /> Copy magic link
                    </button>
                    <button onClick={() => sendWelcome(a.slug, a.label)}
                      disabled={sendingFor === a.slug}
                      data-testid={`send-welcome-${a.slug}`}
                      title="Send credentials (legacy — use Share Trial for magic link)"
                      className="flex-1 h-8 px-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/75 text-[11px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                      {sendingFor === a.slug
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Send className="w-3 h-3" />}
                      Send password
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Timeline modal — Hotjar light */}
      {timelineFor && (
        <div className="fixed inset-0 z-50 bg-black/65 flex items-center justify-center p-4"
             data-testid="timeline-modal">
          <div className="bg-[#0F0820] border border-emerald-500/30 rounded-xl p-6 max-w-lg w-full text-white">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-5 h-5 text-emerald-400" />
              <h3 className="text-[15px] font-bold flex-1">Activity timeline · {timelineFor.label}</h3>
              <button onClick={() => setTimelineFor(null)} className="text-white/55 hover:text-white text-[18px] leading-none">×</button>
            </div>
            <p className="text-[11.5px] text-white/55 mb-4">
              Tudo que esse prospect fez na plataforma — eventos do servidor + open/click de e-mail.
            </p>
            {timelineLoading ? (
              <div className="py-10 text-center text-white/55 inline-flex items-center justify-center gap-2 w-full">
                <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
              </div>
            ) : timelineItems.length === 0 ? (
              <div className="py-10 text-center text-white/55 text-[12.5px]">
                Sem atividade registrada ainda. Quando o prospect abrir o magic link ou agir no app, vai aparecer aqui.
              </div>
            ) : (
              <ul className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                {timelineItems.map((it, i) => {
                  const action = it.action || "";
                  const isEmail = action.startsWith("email.");
                  const isAuth = action.includes("login") || action.includes("magic_link");
                  const color = isEmail
                    ? "border-amber-400/40 bg-amber-500/10 text-amber-200"
                    : isAuth
                    ? "border-purple-400/40 bg-purple-500/10 text-purple-200"
                    : "border-emerald-400/40 bg-emerald-500/10 text-emerald-200";
                  const ts = (it.ts || "").replace("T", " ").slice(0, 19);
                  const detailParts = [];
                  if (it.details?.lead_id) detailParts.push(`lead ${String(it.details.lead_id).slice(0, 8)}`);
                  if (it.details?.subject) detailParts.push(`"${it.details.subject}"`);
                  if (it.details?.keyword) detailParts.push(`keyword "${it.details.keyword}"`);
                  if (it.details?.location) detailParts.push(`@ ${it.details.location}`);
                  return (
                    <li key={i} className={`flex items-center gap-2 px-3 py-2 rounded border ${color} text-[12px]`}
                        data-testid={`timeline-row-${i}`}>
                      <span className="font-mono-tech text-[10.5px] opacity-65 w-32 shrink-0">{ts}</span>
                      <span className="font-bold">{action}</span>
                      {detailParts.length > 0 && (
                        <span className="opacity-65 truncate">· {detailParts.join(" · ")}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Share Trial modal — magic link + branded email in one shot */}
      {shareModal && (
        <div className="fixed inset-0 z-50 bg-black/65 flex items-center justify-center p-4"
             data-testid="share-trial-modal">
          <div className="bg-[#0F0820] border border-[#C9A24E]/30 rounded-xl p-6 max-w-md w-full text-white">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-5 h-5 text-[#C9A24E]" />
              <h3 className="text-[15px] font-bold">Share Trial · {shareModal.label}</h3>
            </div>
            <p className="text-[12px] text-white/65 mb-4">
              Magic link de 14 dias + e-mail brand-styled enviado pela Resend. Sem senha — só clicar e entrar.
            </p>
            <div className="space-y-3">
              <Field label="Destinatário" placeholder="prospect@empresa.com.br"
                value={shareForm.to} onChange={(v) => setShareForm({ ...shareForm, to: v })}
                testid="share-trial-to" />
              <Field label="Nome do destinatário (opcional)" placeholder="Carlos"
                value={shareForm.recipient_name} onChange={(v) => setShareForm({ ...shareForm, recipient_name: v })}
                testid="share-trial-name" />
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-1">Mensagem pessoal (opcional)</label>
                <textarea
                  rows={3}
                  value={shareForm.custom_message}
                  onChange={(e) => setShareForm({ ...shareForm, custom_message: e.target.value })}
                  placeholder="Ex.: Conforme conversamos, segue o acesso. Qualquer dúvida me chama!"
                  data-testid="share-trial-message"
                  className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-[13px] focus:border-[#C9A24E] outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-1">Idioma</label>
                  <select value={shareForm.lang}
                    onChange={(e) => setShareForm({ ...shareForm, lang: e.target.value })}
                    data-testid="share-trial-lang"
                    className="w-full bg-white/5 border border-white/15 rounded-lg px-3 h-9 text-[13px]">
                    <option value="pt">Português</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-1">Validade</label>
                  <select value={shareForm.ttl_days}
                    onChange={(e) => setShareForm({ ...shareForm, ttl_days: Number(e.target.value) })}
                    data-testid="share-trial-ttl"
                    className="w-full bg-white/5 border border-white/15 rounded-lg px-3 h-9 text-[13px]">
                    <option value={3}>3 dias</option>
                    <option value={7}>7 dias</option>
                    <option value={14}>14 dias</option>
                    <option value={30}>30 dias</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={sendShareTrial} disabled={shareBusy || !shareForm.to}
                data-testid="share-trial-send"
                className="flex-1 h-10 rounded-lg bg-[#C9A24E] hover:bg-[#b48d3e] text-[#1A0E36] font-bold text-[12.5px] inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {shareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Enviar trial
              </button>
              <button onClick={() => setShareModal(null)}
                className="px-4 h-10 rounded-lg border border-white/15 text-[12.5px] font-bold text-white/75 hover:bg-white/5">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
}

function Field({ label, placeholder, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white/5 border border-white/15 rounded-lg px-3 h-10 text-[13px] focus:border-[#C9A24E] outline-none"
      />
    </div>
  );
}

function CredRow({ label, value, onCopy, mono }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10.5px] uppercase tracking-wider text-white/45 font-semibold w-16 shrink-0">{label}</span>
      <code className={`flex-1 truncate text-white/85 ${mono ? "font-mono-tech" : ""}`}>{value}</code>
      <button onClick={onCopy}
        className="text-white/55 hover:text-[#C9A24E] p-1.5 rounded hover:bg-white/5"
        title="Copy">
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
